import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Grid } from '@mui/material';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Loader from '../../../components/Loader';
import SectionHeader from '../../components/SectionHeader';
import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { FUNCTIONALITY } from '../../../constants/types';
import { HOSPITAL_QUALITY_CHECK_VISIT, ROUTE_401 } from '../../../routes/urls';
import VisitQueue, { QcQueueCorrectionFilter, QcQueueDiagnosisFilter, QcQueueStateFilter } from './VisitQueue';
import { useQcQueue } from './useQcQueue';
import { canUseCorrectionFlag } from './correctionFlag';

interface QualityCheckProps {
    investigations?: {
        loading?: boolean;
        currentInvestigation?: {
            uuid: string;
            permissions?: string[];
            functionalities?: string[];
        };
    };
}

/**
 * SKIN-239 — landing screen of the Quality Check section: the per-visit review queue.
 *
 * The sidebar already hides this section without APPROVE_VISIT + CLINICAL, so reaching
 * here means someone typed the URL. Both gates are re-checked rather than trusted,
 * because the sidebar is a display concern and the route is reachable without it.
 */
export function QualityCheck(props: QualityCheckProps) {
    const history = useHistory();
    const [stateFilter, setStateFilter] = useState<QcQueueStateFilter>('all');
    // SKIN-441 — its own state, not a member of stateFilter: the two axes are
    // independent and the queue ANDs them.
    const [diagnosisFilter, setDiagnosisFilter] = useState<QcQueueDiagnosisFilter>('all');
    // SKIN-577 — the third axis, offered only where the study allows correction flags.
    const [correctionFilter, setCorrectionFilter] = useState<QcQueueCorrectionFilter>('all');
    const investigation = props.investigations?.currentInvestigation;
    const canReview = investigation?.permissions?.includes(PERMISSION.APPROVE_VISIT);
    const isClinical = investigation?.functionalities?.includes(FUNCTIONALITY.CLINICAL);

    // The gate is computed above the hook so an unauthorized visitor never issues the
    // request at all. Hooks cannot be called conditionally, but the hook already no-ops
    // on a missing uuid, so withholding the investigation is the conditional.
    const authorized = !!(canReview && isClinical);
    const correctionEnabled = authorized && canUseCorrectionFlag(investigation);
    // Neither filter is a fetch parameter: one request returns every reviewable visit
    // with its state and its lesion counts, and VisitQueue narrows it. Changing either
    // must not re-run this hook.
    const { data, loading, error, retry } = useQcQueue(authorized ? investigation : undefined);

    // The visit-detail container is SKIN-262's; this only takes the reviewer to the
    // route that ticket specifies verbatim, so the two cannot disagree on the path.
    // Until that container is mounted the destination 404s.
    const openVisit = (uuidVisit: string) =>
        history.push(HOSPITAL_QUALITY_CHECK_VISIT.replace(':uuidVisit', uuidVisit));

    if (!props.investigations || props.investigations.loading || !investigation) {
        return <Loader />;
    }

    if (!authorized) {
        history.push(ROUTE_401);
        return <Loader />;
    }

    return (
        <React.Fragment>
            <Helmet title="Quality Check" />
            <Grid container spacing={6}>
                <Grid item xs={12} style={{ color: 'white' }}>
                    <SectionHeader section="quality-check" edit={false} />
                </Grid>
                <Grid item xs={12} style={{ background: 'white', padding: '2rem' }}>
                    <VisitQueue
                        loading={loading}
                        error={error}
                        rows={data}
                        stateFilter={stateFilter}
                        onStateFilterChange={setStateFilter}
                        diagnosisFilter={diagnosisFilter}
                        onDiagnosisFilterChange={setDiagnosisFilter}
                        correction={correctionEnabled
                            ? { filter: correctionFilter, onFilterChange: setCorrectionFilter }
                            : undefined}
                        onRetry={retry}
                        onSelectVisit={openVisit}
                    />
                </Grid>
            </Grid>
        </React.Fragment>
    );
}

const mapStateToProps = (state: any) => ({
    investigations: state.investigations,
});

export default connect(mapStateToProps, null)(QualityCheck);
